import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaHistory, FaFileAlt, FaFileExcel, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./style.css";
import axios from "axios";
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../js/api.js";
import Loader from "../../components/Loader/index.js";
import {
  resolverAtorEvento,
  ATOR_LABEL,
} from "./mappers/atorMapper.js";
import {
  ehReferenciaValida,
  anexarReferenciasAceitas,
} from "./utils/referencia.js";
import {
  identificarPerfilHistoricoComLS,
} from "./utils/perfil.js";
import {
  listarAutorizacoesDoEstudante,
  tokenEhDeEstudante,
} from "./services/autorizacoesEstudante.js";
import {
  formatarDataHora,
  formatarCnpjCpf,
  formatarCampoAlterado,
  obterValorParaXlsx,
} from "./utils/normalizarHistorico.js";

// Utilitários de data/hora, moeda e tipos para exibição e XLSX centralizados em ./utils/normalizarHistorico.js

const HistoricoEventos = () => {
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState([]);
  const [erroApi, setErroApi] = useState("");
  const [filtroCnpjCpf, setFiltroCnpjCpf] = useState("");
  const [filtroCnpjCpfAplicado, setFiltroCnpjCpfAplicado] = useState("");
  const [filtroAutor, setFiltroAutor] = useState("__SELECIONE__");
  const [filtroAutorAplicado, setFiltroAutorAplicado] =
    useState("__SELECIONE__");
  const [filtroEvento, setFiltroEvento] = useState("__SELECIONE__");
  const [filtroEventoAplicado, setFiltroEventoAplicado] =
    useState("__SELECIONE__");
  const [filtrosAplicados, setFiltrosAplicados] = useState(false);
  // Vínculo do usuário logado para o filtro inicial. Para o perfil
  // empresa, este state guarda o `empresa_id` do JWT (ou do localStorage
  // como fallback) — usado tanto na query string quanto no filtro
  // client-side de defesa em profundidade.
  const [empresaIdFiltro, setEmpresaIdFiltro] = useState("");
  // Diagnóstico: qual perfil foi detectado e qual chave de vínculo foi
  // usada para a busca inicial. Só é populado no mount e é útil para
  // debug do filtro.
  const [perfilDetectado, setPerfilDetectado] = useState(null);
  // ============================================================
  // ESTUDANTE: lista de autorizações vinculadas ao CPF.
  // ============================================================
  // O mesmo CPF pode ter várias autorizações (estagiário vinculado a
  // mais de uma empresa, ou múltiplos ciclos). Cada item do array é
  // UMA autorização individual (TCE ou TCA-Empregador), e o usuário
  // pode filtrar o histórico por uma delas — sem misturar eventos
  // de processos diferentes.
  const [autorizacoesEstudante, setAutorizacoesEstudante] = useState([]);
  const [carregandoAutorizacoes, setCarregandoAutorizacoes] = useState(false);
  // Filtro de autorização aplicada. Vazio ("") = sem filtro
  // (mostra todas as autorizações do CPF). Quando preenchido, é
  // formatado como `"<tipoPrograma>:<id>"`.
  const [filtroAutorizacao, setFiltroAutorizacao] = useState("");
  const [filtroAutorizacaoAplicado, setFiltroAutorizacaoAplicado] =
    useState("");
  const filtroAutorizacaoRef = useRef("");
  const mountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  // Ref espelhada do empresaIdFiltro — necessária porque
  // `buscarEventos` é uma closure recriada a cada render e precisa
  // do valor atual de `empresaIdFiltro` no momento da chamada. Usar
  // state direto aqui capturaria o valor "velho" da primeira render.
  // Sincronizada via useEffect abaixo.
  const empresaIdRef = useRef("");

  // Mantém a ref espelhada em sincronia com o state.
  useEffect(() => {
    empresaIdRef.current = empresaIdFiltro;
  }, [empresaIdFiltro]);

  // Mantém a ref do filtro de autorização em sincronia com o state.
  // Necessário porque `buscarEventos` é uma closure recriada a cada
  // render e precisa do valor atual no momento da chamada.
  useEffect(() => {
    filtroAutorizacaoRef.current = filtroAutorizacaoAplicado;
  }, [filtroAutorizacaoAplicado]);

  // Garante que qualquer setState disparado após o componente ser desmontado
  // seja ignorado. Em React 18 Strict Mode o componente desmonta e remonta
  // rapidamente em dev, e isso já causou o sintoma de "loading infinito".
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancela qualquer request em voo quando o componente desmonta,
      // evitando pendurar Promises e o loader.
      if (abortControllerRef.current) {
        try {
          abortControllerRef.current.abort();
        } catch (e) {
          /* noop */
        }
        abortControllerRef.current = null;
      }
    };
  }, []);

  const setCarregandoSeguro = (valor) => {
    if (mountedRef.current) setCarregando(valor);
  };
  const setItensSeguro = (valor) => {
    if (mountedRef.current) setItens(valor);
  };
  const setErroApiSeguro = (valor) => {
    if (mountedRef.current) setErroApi(valor);
  };

  const buscarEventos = async (
    cnpjCpf = "",
    autor = "",
    evento = "",
    autorizacaoFiltro = "",
  ) => {
    // Limite duro de páginas para impedir "loading infinito" caso o backend
    // retorne um `pages` absurdamente alto (ex.: bug de paginação).
    const MAX_PAGINAS = 50;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setCarregandoSeguro(true);
    const t0 = Date.now();
    // Debug persistente: habilite no console com `window.__historicoDebug = true`
    // e dê F5. Os logs aparecem mesmo em build de produção.
    const log = (...args) => {
      if (typeof window !== "undefined" && window.__historicoDebug) {
        // eslint-disable-next-line no-console
        console.log("[HistoricoEventos][DEBUG]", ...args);
      }
    };
    log("buscarEventos iniciado", {
      cnpjCpf,
      autor,
      evento,
      autorizacaoFiltro,
    });
    try {
      const token = localStorage.getItem("token");
      // Sem token: o interceptor global de 401 (axiosInterceptors.js)
      // cuida de redirecionar para o login. Aqui só retornamos sem
      // chamar a API para evitar poluir o console com erro de rede.
      if (!token) {
        log("sem token no localStorage — abortando busca");
        return;
      }
      const cnpjNumeros = String(cnpjCpf).replace(/\D/g, "");

      // `autorizacaoFiltro` é o id da autorização selecionada (string
      // numérica) ou vazio. Quando preenchido, enviamos como
      // `autorizacao_id` na query para o backend (quando respeita o
      // filtro) já trazer só os eventos daquela autorização. Mesmo
      // que o backend ignore, o filtro client-side em `itensFiltrados`
      // compara o `referencia_id`/`ref_id` de cada item.
      const autorizacaoIdStr = String(autorizacaoFiltro || "").trim();

      const montarUrl = (paramsBase) => {
        const params = new URLSearchParams(paramsBase);
        if (cnpjNumeros) params.set("cpf_cnpj", cnpjNumeros);
        if (autor) params.set("autor", autor);
        if (evento) params.set("evento", evento);
        if (autorizacaoIdStr) params.set("autorizacao_id", autorizacaoIdStr);
        // Para o perfil empresa, enviamos também o `empresa_id` como
        // chave de vínculo. O backend pode usar este campo (se
        // implementado) OU cair de volta no `cpf_cnpj` (CNPJ) acima.
        // O filtro client-side em `itensFiltrados` ainda compara o
        // `empresa_id` do payload como rede de segurança.
        if (empresaIdRef.current) {
          params.set("empresa_id", empresaIdRef.current);
        }
        // Defesa contra vazamento de outros módulos no /historico-eventos:
        // Antes (até 2026-09-25) essa função anexava múltiplos
        // `referencia_tipo` na query — alguns backends rejeitam a chave
        // duplicada com `400 Bad Request`. Agora é no-op e a defesa
        // fica 100% em `itensFiltrados` via `ehReferenciaValida`.
        anexarReferenciasAceitas(params);
        return `${API_BASE_URL}/historico-eventos?${params.toString()}`;
      };

      // Helper de request com timeout: evita que uma página pendurada
      // trave o Promise.all indefinidamente (sintoma do "loading infinito"
      // reportado pela colega que mantinha a tela antes).
      const requestComTimeout = (url, { timeoutMs = 15000 } = {}) => {
        log("GET", url, "timeoutMs=", timeoutMs);
        return axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: timeoutMs,
          signal: controller.signal,
        });
      };

      try {
        log("disparando request página 1…");
        const primeiraResp = await requestComTimeout(
          montarUrl(new URLSearchParams()),
        );
        log("resposta página 1 recebida em", Date.now() - t0, "ms", {
          status: primeiraResp?.status,
          contentType: primeiraResp?.headers?.["content-type"],
        });
        // A API pode responder com:
        //  - array simples: [...]
        //  - paginado: { total, page, limit, pages, data: [...] }
        //  - erro: { errors: ["..."] } (HTTP 200 mesmo assim)
        //  - Sequelize: { count, rows }
        const payload = primeiraResp.data;
        log("payload bruto página 1:", payload);
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
          log("chaves do payload:", Object.keys(payload));
          log("Array.isArray(payload.data)?", Array.isArray(payload?.data));
          log("Array.isArray(payload.rows)?", Array.isArray(payload?.rows));
          log("payload.pages=", payload?.pages, "typeof=", typeof payload?.pages);
          log("payload.total=", payload?.total);
          log("payload.errors=", payload?.errors);
          log("payload.data length=", Array.isArray(payload?.data) ? payload.data.length : "N/A");
        }

        // Se o backend já devolveu tudo em um único array, usa direto.
        if (Array.isArray(payload)) {
          setItensSeguro(payload);
          return;
        }

        // Resposta com erro explícito.
        if (payload?.errors) {
          setErroApiSeguro(payload.errors.join(" • "));
          setItensSeguro([]);
          return;
        }

        // Resposta paginada { total, page, limit, pages, data }.
        if (Array.isArray(payload?.data)) {
          const totalPagesBruto = Number(payload.pages) || 1;
          const totalPages = Math.min(
            Math.max(totalPagesBruto, 1),
            MAX_PAGINAS,
          );
          log("ENTROU no ramo paginado. totalPagesBruto=", totalPagesBruto, "totalPages=", totalPages, "data.length=", payload.data.length);
          if (totalPagesBruto > MAX_PAGINAS) {
            console.warn(
              `[HistoricoEventos] backend devolveu pages=${totalPagesBruto}; truncando para ${MAX_PAGINAS} por segurança.`,
            );
          }
          let lista = [...payload.data];
          // Percorre as páginas restantes em paralelo (se houver mais que 1).
          // Cada requisição é iniciada imediatamente; a ordem final na lista
          // segue a mesma sequência numérica das páginas (2, 3, ... N),
          // independentemente da ordem em que as respostas chegam.
          if (totalPages > 1) {
            const promessasPaginas = [];
            for (let p = 2; p <= totalPages; p += 1) {
              const proxUrl = montarUrl(
                new URLSearchParams([["page", String(p)]]),
              );
              log("disparando request página", p, proxUrl);
              promessasPaginas.push(
                requestComTimeout(proxUrl)
                  .then((proxResp) => {
                    const proxDados = proxResp.data?.data || proxResp.data;
                    log("página", p, "OK, dados length=", Array.isArray(proxDados) ? proxDados.length : "N/A");
                    return { pagina: p, dados: proxDados };
                  })
                  .catch((errPagina) => {
                    // Falha em uma página específica: loga mas não derruba tudo.
                    log("página", p, "FALHOU:", errPagina?.message || errPagina);
                    console.warn(
                      "[HistoricoEventos] falha ao buscar página",
                      p,
                      errPagina,
                    );
                    return { pagina: p, dados: [] };
                  }),
              );
            }

            log("aguardando Promise.all de", promessasPaginas.length, "páginas…");
            const resultadosPaginas = await Promise.all(promessasPaginas);
            log("Promise.all resolvido");
            // Garante a mesma ordem do loop original (página 2 → página N),
            // mesmo que as respostas cheguem fora de ordem.
            resultadosPaginas
              .sort((a, b) => a.pagina - b.pagina)
              .forEach((res) => {
                if (Array.isArray(res.dados)) lista.push(...res.dados);
              });
          }
          log("setItensSeguro com", lista.length, "itens");
          setItensSeguro(lista);
          return;
        }

        // Sequelize findAndCountAll { count, rows }.
        if (Array.isArray(payload?.rows)) {
          log("ENTROU no ramo Sequelize rows, length=", payload.rows.length);
          setItensSeguro(payload.rows);
          return;
        }

        log("NENHUM ramo conhecido bateu — setItensSeguro([])");
        setItensSeguro([]);
      } catch (err) {
        log("catch interno:", err?.response?.status, err?.message);
        if (err?.response?.status !== 404 && err?.response?.status !== 405) {
          throw err;
        }
      }

      log("fim do try interno sem setItens — setItensSeguro([]) fallback");
      setItensSeguro([]);
    } catch (err) {
      log("catch externo:", err?.response?.status, err?.message);
      if (axios.isCancel(err)) {
        // Componente desmontou no meio do request — não faz nada.
        log("request foi cancelado (unmount) — ignorando");
        return;
      }
      // 401 é tratado globalmente pelo interceptor em
      // `src/js/axiosInterceptors.js` (dispara `auth:logout` →
      // redireciona para /login). Aqui só logamos para diagnóstico
      // e zeramos a lista.
      if (err?.response?.status === 401) {
        console.warn(
          "[HistoricoEventos] 401 — interceptor global vai redirecionar.",
        );
        setItensSeguro([]);
        return;
      }
      console.error("[HistoricoEventos] erro geral:", err);
      setItensSeguro([]);
    } finally {
      log("finally: setCarregandoSeguro(false), durou", Date.now() - t0, "ms");
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
      setCarregandoSeguro(false);
    }
  };

  // No mount: SEMPRE dispara a busca inicial (sem filtros). Para o
// estudante, pré-aplicamos o CPF dele no filtro para evitar mistura
// de eventos entre diferentes autorizações. Para os demais perfis
// (empresa / CIEE / consultor), a busca inicial vem sem filtro e
// mostra todos os eventos visíveis.
//
// BUG HISTÓRICO: antes deste fix, a busca inicial só era disparada
// se o usuário fosse estudante. Empresa/CIEE/consultor chegavam na
// página e o request NUNCA era feito, deixando o `carregando` (que
// começa como `true`) preso para sempre — sintoma do "loading
// infinito" reportado.
useEffect(() => {
  const token = localStorage.getItem("token");
  if (!token) {
    // Sem token: libera o loader para o usuário ver a tela vazia
    // em vez de ficar preso.
    setCarregando(false);
    return;
  }

  let cpfParaFiltro = "";
  let cnpjParaFiltro = "";
  let empresaIdParaFiltro = "";
  let perfilResumo = null;
  let isEstudante = false;
  try {
    const decoded = jwtDecode(token);
    const perfil = identificarPerfilHistoricoComLS(
      decoded,
      typeof window !== "undefined" ? window.localStorage : null,
    );
    perfilResumo = {
      isEstudante: perfil.isEstudante,
      isEmpresa: perfil.isEmpresa,
      isCieemg: perfil.isCieemg,
      nivel: perfil.nivel,
      cpf: perfil.cpf,
      cnpj: perfil.cnpj,
      empresaId: perfil.empresaId,
    };
    isEstudante = perfil.isEstudante;

    if (perfil.isEstudante && perfil.cpf) {
      // Estudante: pré-aplica o CPF dele no filtro (vínculo por CPF).
      cpfParaFiltro = perfil.cpf;
      setFiltroCnpjCpf(cpfParaFiltro);
      setFiltroCnpjCpfAplicado(cpfParaFiltro);
      setFiltrosAplicados(true);
    } else if (perfil.isEmpresa) {
      // Empresa: pré-aplica o CNPJ como `cpf_cnpj` na query (alguns
      // backends indexam por CNPJ mesmo) e o `empresa_id` como chave
      // forte. Sem empresa_id não há como garantir o vínculo — nesse
      // caso abrimos um aviso no console mas ainda assim aplicamos o
      // CNPJ como fallback.
      cnpjParaFiltro = perfil.cnpj || "";
      empresaIdParaFiltro = perfil.empresaId || "";
      if (cnpjParaFiltro) {
        setFiltroCnpjCpf(cnpjParaFiltro);
        setFiltroCnpjCpfAplicado(cnpjParaFiltro);
        setFiltrosAplicados(true);
      }
      if (empresaIdParaFiltro) {
        setEmpresaIdFiltro(empresaIdParaFiltro);
      }
      if (!empresaIdParaFiltro) {
        // eslint-disable-next-line no-console
        console.warn(
          "[HistoricoEventos] perfil empresa sem empresa_id — o vínculo por ID da empresa não pode ser aplicado.",
          perfil,
        );
      }
    }
    // CIEEMG: vê tudo, sem pré-filtro de vínculo.
  } catch (e) {
    /* silencioso: cai no fluxo padrão (busca sem filtro) */
  }

  if (perfilResumo) {
    setPerfilDetectado(perfilResumo);
    if (typeof window !== "undefined" && window.__historicoDebug) {
      // eslint-disable-next-line no-console
      console.log("[HistoricoEventos][DEBUG] perfil detectado:", perfilResumo);
    }
  }

  // ESTUDANTE: carrega lista de autorizações do CPF.
  // Cada item é UMA autorização individual (TCE ou TCA-Empregador).
  // Mesmo CPF pode ter várias (mesmo estagiário vinculado a múltiplas
  // empresas ou múltiplos ciclos). Pré-seleciona a vinda em
  // `?id=<id>` na URL (compatibilidade com o clique vindo de
  // Acompanhamento / Preenchimento); se não houver `?id=`, mostra
  // todas as autorizações sem pré-filtrar.
  if (isEstudante && tokenEhDeEstudante(token)) {
    let autorizacaoInicialId = "";
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const idDaUrl = urlParams.get("id");
      if (idDaUrl && /^\d+$/.test(idDaUrl)) {
        autorizacaoInicialId = idDaUrl;
      }
    } catch (_) {
      autorizacaoInicialId = "";
    }

    setCarregandoAutorizacoes(true);
    listarAutorizacoesDoEstudante(token)
      .then((lista) => {
        if (!mountedRef.current) return;
        setAutorizacoesEstudante(Array.isArray(lista) ? lista : []);
        // Se houver id vindo da URL, pré-aplica. Caso contrário,
        // mostra todas as autorizações do CPF (sem filtro).
        if (autorizacaoInicialId) {
          setFiltroAutorizacao(autorizacaoInicialId);
          setFiltroAutorizacaoAplicado(autorizacaoInicialId);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(
          "[HistoricoEventos] falha ao listar autorizações do estudante:",
          err?.message,
        );
      })
      .finally(() => {
        if (mountedRef.current) setCarregandoAutorizacoes(false);
      });
  }

  // SEMPRE dispara a busca inicial, com ou sem filtro de CPF.
  // Para empresa, o filtro CNPJ já foi aplicado acima; para
  // estudante, o filtro CPF; para CIEE, nenhum.
  //
  // ESTUDANTE: a busca inicial NÃO aplica filtro de autorização —
  // queremos mostrar o universo completo do CPF no primeiro paint.
  // O usuário (ou o clique vindo do Acompanhamento via `?id=`) pode
  // restringir uma autorização específica depois.
  const autorizacaoInicial = (() => {
    if (!isEstudante) return "";
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const idDaUrl = urlParams.get("id");
      if (idDaUrl && /^\d+$/.test(idDaUrl)) return idDaUrl;
    } catch (_) {
      /* silencioso */
    }
    return "";
  })();
  if (autorizacaoInicial) {
    setFiltroAutorizacao(autorizacaoInicial);
    setFiltroAutorizacaoAplicado(autorizacaoInicial);
  }
  buscarEventos(cpfParaFiltro || cnpjParaFiltro, "", "", autorizacaoInicial);
  // Sem cleanup explícito aqui — o mountedRef + AbortControllerRef
  // declarados no useEffect de "unmount watcher" acima já cancelam
  // requests em voo e bloqueiam setState tardios.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  const aplicarFiltro = () => {
    const autorAplicado = filtroAutor === "__SELECIONE__" ? "" : filtroAutor;

    const eventoAplicado = filtroEvento === "__SELECIONE__" ? "" : filtroEvento;

    setFiltroCnpjCpfAplicado(filtroCnpjCpf.trim());
    setFiltroAutorAplicado(autorAplicado);
    setFiltroEventoAplicado(eventoAplicado);
    setFiltroAutorizacaoAplicado(filtroAutorizacao);
    setFiltrosAplicados(true);
    setErroApi("");

    buscarEventos(
      filtroCnpjCpf.trim(),
      autorAplicado,
      eventoAplicado,
      filtroAutorizacao,
    );
  };

  const limparFiltro = () => {
    setFiltroCnpjCpf("");
    setFiltroCnpjCpfAplicado("");
    setFiltroAutor("__SELECIONE__");
    setFiltroAutorAplicado("__SELECIONE__");
    setFiltroEvento("__SELECIONE__");
    setFiltroAutorizacao("");
    setFiltroAutorizacaoAplicado("");
    setFiltroEventoAplicado("__SELECIONE__");
    setFiltrosAplicados(false);
    buscarEventos("", "", "", "");
  };

  const itensFiltrados = useMemo(() => {
    let lista = itens;

    // Deduplica por `id` (ou pela assinatura cpf+data+evento quando
    // o id não vem). O backend tem apresentado registros com IDs
    // repetidos no payload (problema de criação duplicada no banco),
    // o que (a) enche o console com warning de "two children with
    // the same key" e (b) duplica visualmente a mesma linha na
    // tabela. Mantemos a primeira ocorrência para preservar ordem.
    const vistos = new Set();
    const semIdVistos = new Set();
    const dedup = [];
    for (let i = 0; i < lista.length; i += 1) {
      const it = lista[i];
      if (it && (it.id !== undefined && it.id !== null && it.id !== "")) {
        const idStr = String(it.id);
        if (vistos.has(idStr)) continue;
        vistos.add(idStr);
        dedup.push(it);
      } else {
        // Sem id: usa assinatura cpf+data+evento+índice como chave única.
        const sig = `${it?.cpf_cnpj || it?.cnpj_cpf || ""}|${it?.data_hora || it?.data || it?.created_at || ""}|${it?.evento || ""}|${i}`;
        if (semIdVistos.has(sig)) continue;
        semIdVistos.add(sig);
        dedup.push(it);
      }
    }
    lista = dedup;

    // IMPORTANTE: o filtro de cpf_cnpj é feito pelo backend (via querystring
    // em /historico-eventos?cpf_cnpj=...). Aqui NÃO filtramos de novo,
    // senaria duplicamos a lógica e podemos mascarar divergências entre
    // o que o backend entende como "match" e o que o front entende.

    // Defesa em profundidade: descarta itens de outros módulos (ex.:
    // Cadastro Empresa) que porventura o backend tenha devolvido.
    // O `ehReferenciaValida` é tolerante — se o backend omitir o campo
    // `referencia_tipo` (legado), o item passa.
    const antesFiltroRef = lista.length;
    lista = lista.filter(ehReferenciaValida);
    const descartadosPorReferencia = antesFiltroRef - lista.length;
    if (descartadosPorReferencia > 0) {
      if (typeof window !== "undefined" && window.__historicoDebug) {
        // eslint-disable-next-line no-console
        console.log(
          "[HistoricoEventos][DEBUG] descartados por referencia_tipo fora da lista branca:",
          descartadosPorReferencia,
        );
      }
    }

    // Enriquece cada item com `ator_calculado` (canônico EMPRESA / ESTUDANTE
    // / CIEE / SISTEMA). Isso garante que o badge mostre o ator correto
    // mesmo quando o backend grava `autor = "sistema"` para tudo.
    lista = lista.map((item) => ({
      ...item,
      ator_calculado: resolverAtorEvento(item),
    }));

    // Defesa em profundidade — vínculo pela empresa.
    // Quando o usuário logado é uma empresa e temos o `empresa_id`
    // dele, descartamos todo item do payload que traga um
    // `empresa_id` (ou variantes) diferente do nosso. O backend já
    // deveria ter filtrado pela query string, mas se o JOIN estiver
    // quebrado (mesma família de bugs do card Acompanhamento),
    // esta camada protege o vínculo.
    if (empresaIdFiltro) {
      const idNorm = String(empresaIdFiltro).trim();
      const antesFiltroEmp = lista.length;
      lista = lista.filter((item) => {
        // Tenta vários nomes de campo porque o backend pode
        // expor o vínculo em qualquer um destes (ou em nenhum,
        // caso legado).
        const candidatos = [
          item.empresa_id,
          item.empresaId,
          item.id_empresa,
          item.idEmpresa,
          // Às vezes vem aninhado:
          item?.empresa?.id,
          item?.empresa?.empresa_id,
        ];
        for (const cand of candidatos) {
          if (cand === undefined || cand === null || cand === "") continue;
          if (String(cand).trim() === idNorm) return true;
        }
        // Se o item NÃO traz nenhum campo de empresa_id (legado),
        // optamos por MANTER (pass-through) para não esvaziar a
        // lista quando o backend não devolve o vínculo. A regra
        // estrita só descarta quando há evidência de empresa
        // diferente.
        return true;
      });
      const descartadosPorEmpresa = antesFiltroEmp - lista.length;
      if (descartadosPorEmpresa > 0) {
        if (typeof window !== "undefined" && window.__historicoDebug) {
          // eslint-disable-next-line no-console
          console.log(
            "[HistoricoEventos][DEBUG] descartados por empresa_id diferente do logado:",
            descartadosPorEmpresa,
          );
        }
      }
    }

    if (filtroAutorAplicado && filtroAutorAplicado !== "__SELECIONE__") {
      // Quando o filtro é uma das categorias canônicas (EMPRESA/ESTUDANTE/
      // CIEE/SISTEMA), compara pelo `ator_calculado` em vez do `autor` do
      // backend. Quando é uma string livre (legado), cai no `autor` original.
      const CANONICOS = ["EMPRESA", "ESTUDANTE", "CIEE", "SISTEMA"];
      const filtroUpper = filtroAutorAplicado.toUpperCase();
      if (CANONICOS.includes(filtroUpper)) {
        lista = lista.filter(
          (item) => String(item.ator_calculado || "").toUpperCase() === filtroUpper,
        );
      } else {
        lista = lista.filter(
          (item) =>
            String(item.autor || "").toUpperCase() ===
            filtroAutorAplicado.toUpperCase(),
        );
      }
    }

    if (filtroEventoAplicado && filtroEventoAplicado !== "__SELECIONE__") {
      lista = lista.filter((item) => item.evento === filtroEventoAplicado);
    }

    // ESTUDANTE: filtro por autorização individual.
    // Quando o usuário (estudante) seleciona uma autorização específica
    // no seletor, esta camada CLIENT-SIDE garante que mesmo que o
    // backend não respeite o `autorizacao_id` da query, só eventos
    // daquela autorização apareçam. O backend pode mandar `ref_id` ou
    // `referencia_id` ou ainda ambos — toleramos todos os nomes.
    if (filtroAutorizacaoAplicado) {
      const idNorm = String(filtroAutorizacaoAplicado).trim();
      if (idNorm) {
        const antesFiltroAut = lista.length;
        lista = lista.filter((item) => {
          const candidatos = [
            item.referencia_id,
            item.ref_id,
            item.autorizacao_id,
            item.tca_id,
            item.tce_id,
            item.tca_empregador_id,
            // Variações aninhadas (legado):
            item?.autorizacao?.id,
            item?.ref?.id,
            item?.referencia?.id,
          ];
          for (const cand of candidatos) {
            if (cand === undefined || cand === null || cand === "") continue;
            if (String(cand).trim() === idNorm) return true;
          }
          // Sem referência explícita no item → descartar (defesa em
          // profundidade). Diferente do filtro de empresa (que faz
          // pass-through para legado), aqui o vínculo por autorização
          // é EXIGIDO porque sem ele não dá pra saber se o evento é
          // daquela autorização específica.
          return false;
        });
        const descartadosPorAutorizacao = antesFiltroAut - lista.length;
        if (
          descartadosPorAutorizacao > 0 &&
          typeof window !== "undefined" &&
          window.__historicoDebug
        ) {
          // eslint-disable-next-line no-console
          console.log(
            "[HistoricoEventos][DEBUG] descartados por autorizacao_id diferente do filtro:",
            descartadosPorAutorizacao,
          );
        }
      }
    }

    return lista;
  }, [
    itens,
    filtroAutorAplicado,
    filtroEventoAplicado,
    empresaIdFiltro,
    filtroAutorizacaoAplicado,
  ]);

  // Opções dos filtros derivadas dinamicamente dos dados reais retornados
  // pela API. Sem catálogo prévio no frontend: se o backend criar um novo
  // valor de `autor` ou `evento`, ele aparecerá automaticamente aqui assim
  // que vier em qualquer item da resposta.
  const opcoesAutor = useMemo(() => {
    // Calcula o ator canônico para cada item (mesma lógica usada na tabela)
    // e monta as opções a partir dos valores únicos de `ator_calculado`,
    // exibidas com o label humano (ATOR_LABEL).
    const set = new Set();
    for (const item of itens) {
      const canonico = resolverAtorEvento(item);
      if (canonico) set.add(canonico);
    }
    return [
      { value: "", label: "Todos os autores" },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: ATOR_LABEL[v] || v })),
    ];
  }, [itens]);

  const opcoesEvento = useMemo(() => {
    const set = new Set();
    for (const item of itens) {
      const v = String(item?.evento ?? "").trim();
      if (v) set.add(v);
    }
    return [
      { value: "", label: "Todos os eventos" },
      ...Array.from(set)
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v })),
    ];
  }, [itens]);

  const temFiltroAplicado =
    filtrosAplicados &&
    (filtroCnpjCpfAplicado !== "" ||
      (filtroAutorAplicado !== "" && filtroAutorAplicado !== "__SELECIONE__") ||
      (filtroEventoAplicado !== "" &&
        filtroEventoAplicado !== "__SELECIONE__"));

  const obterDataHora = (item) =>
    item.data ||
    item.data_hora ||
    item.dataHora ||
    item.created_at ||
    item.createdAt ||
    item.timestamp ||
    item.updated_at ||
    null;

  const obterCnpjCpf = (item) =>
    item.cpf_cnpj || item.cnpj_cpf || item.cpfCnpj || "";

  const obterUsuario = (item) =>
    item.usuario_nome || item.usuario || item.nome_usuario || item.nome || "";

  // Monta a planilha em memória. Cada chave do objeto vira uma coluna,
  // e a biblioteca xlsx cuida de tudo: tipos, datas, acentos, células.
  const exportarXlsx = async () => {
    try {
      const cabecalho = [
        "Nome do Usuário",
        "CPF ou CNPJ",
        "Data/Hora",
        "Campo Alterado",
        "Valor Anterior",
        "Valor Novo",
        "Autor",
        "Descrição",
        "Status",
      ];

      const linhas = itensFiltrados.map((item) => {
        const anterior = obterValorParaXlsx(
          item.valor_anterior,
          item.campo_alterado,
        );
        const novo = obterValorParaXlsx(item.valor_novo, item.campo_alterado);
        return {
          "Nome do Usuário": obterUsuario(item),
          "CPF ou CNPJ": formatarCnpjCpf(obterCnpjCpf(item)),
          "Data/Hora": obterDataHora(item),
          "Campo Alterado": formatarCampoAlterado(item.campo_alterado),
          "Valor Anterior": anterior.valor,
          "Valor Novo": novo.valor,
          Autor: item.autor || "",
          Descrição: item.descricao || "—",
          Status: item.status ?? "",
        };
      });

      const planilha = XLSX.utils.json_to_sheet(linhas, { header: cabecalho });

      // Aplica formato nativo do Excel nas colunas dinâmicas.
      const colValorAnterior = cabecalho.indexOf("Valor Anterior");
      const colValorNovo = cabecalho.indexOf("Valor Novo");
      const colDataHora = cabecalho.indexOf("Data/Hora");

      itensFiltrados.forEach((item, rowIdx) => {
        const r = rowIdx + 1; // +1 porque a linha 0 é o cabeçalho
        if (colValorAnterior >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorAnterior, r });
          const fmt = obterValorParaXlsx(
            item.valor_anterior,
            item.campo_alterado,
          ).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colValorNovo >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colValorNovo, r });
          const fmt = obterValorParaXlsx(
            item.valor_novo,
            item.campo_alterado,
          ).formato;
          if (planilha[ref] && fmt) planilha[ref].z = fmt;
        }
        if (colDataHora >= 0) {
          const ref = XLSX.utils.encode_cell({ c: colDataHora, r });
          const dh = obterDataHora(item);
          if (planilha[ref] && dh) {
            const d = new Date(dh);
            if (!isNaN(d.getTime())) {
              planilha[ref].t = "d";
              planilha[ref].v = d;
              planilha[ref].z = "dd/mm/yyyy hh:mm";
            }
          }
        }
      });

      // Largura automática das colunas (em caracteres).
      planilha["!cols"] = [
        { wch: 30 }, // Nome do Usuário
        { wch: 18 }, // CPF ou CNPJ
        { wch: 20 }, // Data/Hora
        { wch: 28 }, // Campo Alterado
        { wch: 30 }, // Valor Anterior
        { wch: 30 }, // Valor Novo
        { wch: 14 }, // Autor
        { wch: 40 }, // Atividade
        { wch: 12 }, // Status
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, planilha, "Histórico de Eventos");

      XLSX.writeFile(
        workbook,
        `historico-eventos_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      // Falha ao exportar XLSX: silenciosa para o usuário final.
    }
  };

  const handleVoltar = () => {
    // Para o estudante, voltar do Histórico leva para a tela
    // de Preenchimento (TCE / TCA Emp.), não para o dashboard
    // geral (que é do CIEE/empresa).
    try {
      const tk = localStorage.getItem("token");
      if (tk) {
        const dec = jwtDecode(tk);
        if (
          dec?.tipo === "estudante" ||
          Number(dec?.acesso_autorizacao ?? 0) === 1
        ) {
          const tp = Number(dec?.tipo_programa ?? 0);
          navigate(tp === 2 ? "/tca-empregador" : "/tce", {
            replace: false,
          });
          return;
        }
      }
    } catch (_) {
      /* fallback */
    }
    navigate("/dashboard");
  };

  return (
    <main className="container-historico-eventos">
      <header className="historico-header">
        <div className="historico-titulo-bloco">
          <h1>
            <FaHistory /> Histórico de Registros
          </h1>
          <p className="historico-subtitulo">
            Rastreamento de ações realizadas no sistema.
          </p>
          {perfilDetectado &&
            (perfilDetectado.isEmpresa || perfilDetectado.isEstudante) && (
              <p
                className="historico-contexto-filtro"
                data-testid="historico-contexto-filtro"
              >
                {perfilDetectado.isEmpresa ? (
                  <>
                    Exibindo eventos vinculados à empresa logada
                    {perfilDetectado.empresaId
                      ? ` (ID ${perfilDetectado.empresaId})`
                      : ""}
                    {perfilDetectado.cnpj
                      ? ` — CNPJ ${formatarCnpjCpf(perfilDetectado.cnpj)}`
                      : ""}
                    .
                  </>
                ) : (
                  <>
                    Exibindo eventos vinculados ao estudante logado
                    {perfilDetectado.cpf
                      ? ` — CPF ${formatarCnpjCpf(perfilDetectado.cpf)}`
                      : ""}
                    {filtroAutorizacaoAplicado
                      ? (() => {
                          const aut = autorizacoesEstudante.find(
                            (a) =>
                              String(a.id) ===
                              String(filtroAutorizacaoAplicado),
                          );
                          if (aut) {
                            const tpLabel =
                              aut.tipoPrograma === 2
                                ? "TCA Empregador"
                                : aut.tipoPrograma === 0
                                  ? "TCE"
                                  : `Programa ${aut.tipoPrograma}`;
                            return ` — Autorização #${aut.id} (${tpLabel})`;
                          }
                          return ` — Autorização #${filtroAutorizacaoAplicado}`;
                        })()
                      : autorizacoesEstudante.length > 1
                        ? " — todas as autorizações vinculadas"
                        : ""}
                    .
                  </>
                )}
              </p>
            )}
        </div>

        <div className="historico-acoes-topo">
          <button
            type="button"
            className="historico-btn-voltar"
            onClick={handleVoltar}
            title="Voltar para a tela anterior"
          >
            <FaArrowLeft /> Voltar
          </button>
          <button
            type="button"
            className="historico-botao historico-botao-primario"
            onClick={exportarXlsx}
            disabled={carregando}
          >
            <FaFileExcel /> Exportar Documento
          </button>
        </div>
      </header>

      <section className="historico-filtros">
        <div className="historico-filtro-linha">
          <div className="historico-filtro-grupo historico-filtro-grupo--empresa">
            <label htmlFor="filtro-evento-cnpj-cpf">
              FILTRAR POR EMPRESA (CPF/CNPJ)
            </label>
            <input
              id="filtro-evento-cnpj-cpf"
              type="text"
              placeholder="00.000.000/0000-00"
              value={filtroCnpjCpf}
              onChange={(e) => setFiltroCnpjCpf(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") aplicarFiltro();
              }}
            />
          </div>

          {/* ESTUDANTE: seletor de autorização individual.
              Aparece somente para o perfil estudante e quando o
              backend já devolveu a lista de autorizações do CPF.
              Mesmo CPF pode ter várias autorizações; cada item é
              tratado de forma individual para evitar mistura de
              eventos entre processos diferentes. */}
          {perfilDetectado?.isEstudante && autorizacoesEstudante.length > 0 && (
            <div className="historico-filtro-grupo historico-filtro-grupo--autorizacao">
              <label htmlFor="filtro-evento-autorizacao">AUTORIZAÇÃO</label>
              <select
                id="filtro-evento-autorizacao"
                value={filtroAutorizacao}
                onChange={(e) => setFiltroAutorizacao(e.target.value)}
                disabled={
                  carregandoAutorizacoes || autorizacoesEstudante.length === 1
                }
              >
                {autorizacoesEstudante.length > 1 && (
                  <option value="">Todas as autorizações</option>
                )}
                {autorizacoesEstudante.map((aut) => {
                  const tpLabel =
                    aut.tipoPrograma === 2
                      ? "TCA Emp."
                      : aut.tipoPrograma === 0
                        ? "TCE"
                        : `Prog. ${aut.tipoPrograma}`;
                  const doc = aut.documento
                    ? ` (${formatarCnpjCpf(aut.documento)})`
                    : "";
                  return (
                    <option key={`${aut.tipoPrograma}-${aut.id}`} value={aut.id}>
                      #{aut.id} — {tpLabel} — {aut.nome}
                      {doc}
                    </option>
                  );
                })}
              </select>
              {carregandoAutorizacoes && (
                <small
                  style={{
                    display: "block",
                    marginTop: 4,
                    color: "#64748b",
                    fontSize: 11,
                  }}
                >
                  Carregando autorizações…
                </small>
              )}
            </div>
          )}

          <div className="historico-filtro-grupo historico-filtro-grupo--autor">
            <label htmlFor="filtro-evento-autor">AUTOR</label>
            <select
              id="filtro-evento-autor"
              value={filtroAutor}
              onChange={(e) => setFiltroAutor(e.target.value)}
            >
              <option value="__SELECIONE__" disabled>
                Selecione
              </option>
              {opcoesAutor.map((op) => (
                <option key={op.value || "todos-autores"} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="historico-filtro-grupo historico-filtro-grupo--evento">
            <label htmlFor="filtro-evento-tipo">EVENTO</label>
            <select
              id="filtro-evento-tipo"
              value={filtroEvento}
              onChange={(e) => setFiltroEvento(e.target.value)}
            >
              <option value="__SELECIONE__" disabled>
                Selecione
              </option>
              {opcoesEvento.map((op) => (
                <option key={op.value || "todos-eventos"} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="historico-filtro-acoes">
            <button
              type="button"
              className="historico-filtrar"
              onClick={aplicarFiltro}
              disabled={carregando}
            >
              Filtrar
            </button>
            <button
              type="button"
              className="historico-limpar-filtros"
              onClick={limparFiltro}
              disabled={carregando}
            >
              Limpar
            </button>
          </div>
        </div>
      </section>

      <section className="historico-informacoes">
        <div className="historico-informacoes-header">
          <FaHistory />
          <div>
            <h2>Informações do Histórico</h2>
            <p>
              O histórico registra as ações realizadas no cadastro e identifica
              o responsável, a data e o evento executado.
            </p>
          </div>
        </div>
        <div className="historico-informacoes-grid">
          <div className="historico-informacao-item">
            <strong>Usuário responsável</strong>
            <span>Identifica o usuário que realizou a ação no sistema.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Data e hora</strong>
            <span>Registra exatamente quando o evento foi realizado.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Evento realizado</strong>
            <span>Identifica a ação executada no cadastro.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Autor</strong>
            <span>
              Identifica se a ação foi realizada pela empresa, estudante, CIEE
              ou sistema.
            </span>
          </div>
          <div className="historico-informacao-item">
            <strong>CPF ou CNPJ</strong>
            <span>Permite identificar o cadastro relacionado ao evento.</span>
          </div>
          <div className="historico-informacao-item">
            <strong>Status</strong>
            <span>Apresenta o status relacionado ao evento registrado.</span>
          </div>
        </div>
      </section>

      <section className="historico-resultados">
        <p className="historico-resultados-titulo">REGISTROS ENCONTRADOS</p>
        {carregando ? (
          <div className="historico-loader">
            <Loader />
          </div>
        ) : itensFiltrados.length === 0 ? (
          <div className="historico-vazio">
            <FaFileAlt size={48} />
            {erroApi ? (
              <p className="historico-erro-api">⚠️ {erroApi}</p>
            ) : temFiltroAplicado ? (
              <p>Nenhum registro encontrado.</p>
            ) : (() => {
              // Mensagem especifica para o estudante: explica que
              // historico so aparece quando ele tem autorizacoes
              // vinculadas. Sem isso, a tela fica em branco e o aluno
              // pensa que tem bug.
              if (perfilDetectado?.isEstudante) {
                return (
                  <p>
                    Você ainda não possui eventos registrados nas suas
                    autorizações. Inicie um preenchimento no menu
                    Preenchimento para que o histórico seja gerado.
                  </p>
                );
              }
              return (
                <p>
                  Use os filtros acima e clique em <strong>Filtrar</strong>{" "}
                  para buscar os registros.
                </p>
              );
            })()}
          </div>
        ) : (
          <>
            <div className="historico-tabela-wrapper">
              <table className="historico-tabela">
                <thead>
                  <tr>
                    <th>Nome do Usuário</th>
                    <th>CPF ou CNPJ</th>
                    <th>Data/Hora</th>
                    <th>Campo Alterado</th>
                    <th>Valor Anterior</th>
                    <th>Valor Novo</th>
                    <th>Autor</th>
                    <th>Tipo</th>
                    <th>Ref. ID</th>
                    <th>Descrição</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {itensFiltrados.map((item, idx) => {
                    // Gera uma key única estável por item. Sempre inclui
                    // o `idx` no final como tiebreaker para evitar warning
                    // do React "two children with the same key" quando o
                    // backend devolve itens com IDs duplicados (já que
                    // dedup no itensFiltrados preserva ordem, o idx ainda
                    // é único dentro da lista filtrada renderizada).
                    const keyItem =
                      item.id !== undefined && item.id !== null && item.id !== ""
                        ? `${item.id}-${idx}`
                        : `${item.cpf_cnpj || item.cnpj_cpf || "s"}-${item.data_hora || item.data || item.created_at || "s"}-${item.evento || "s"}-${idx}`;
                    // Sanitiza o autor pra virar uma classe CSS válida
                    // (apenas [a-z0-9-_]). Sem isso, autor com espaço,
                    // acento ou caractere especial vira classe inválida
                    // e o navegador enche o console de warning.
                    // IMPORTANTE: usamos `ator_calculado` (canônico
                    // EMPRESA/ESTUDANTE/CIEE/SISTEMA) em vez de `autor`
                    // bruto, porque o backend grava "sistema" em quase
                    // todos os eventos e o mapper infere o ator real
                    // pelo nome do evento (ver mappers/atorMapper.js).
                    const autorCanônico =
                      item.ator_calculado || resolverAtorEvento(item);
                    const labelAutor = ATOR_LABEL[autorCanônico] || autorCanônico;
                    const classeAutor = String(autorCanônico)
                      .toLowerCase()
                      .replace(/[^a-z0-9-_]/g, "-") || "desconhecido";
                    const classeStatus = String(item.status || "")
                      .toLowerCase()
                      .replace(/\s+/g, "-")
                      .replace(/[^a-z0-9-_]/g, "-") || "desconhecido";
                    return (
                    <tr key={keyItem}>
                      <td>
                        {item.usuario_nome ||
                          item.usuario ||
                          item.nome_usuario ||
                          "—"}
                      </td>
                      <td>
                        {formatarCnpjCpf(item.cpf_cnpj || item.cnpj_cpf) || "—"}
                      </td>
                      <td>
                        {formatarDataHora(
                          item.data_hora || item.data || item.created_at,
                        )}
                      </td>
                      <td>{item.campo_alterado ?? "—"}</td>
                      <td>{item.valor_anterior ?? "—"}</td>
                      <td>{item.valor_novo ?? "—"}</td>
                      <td>
                        <span
                          className={`badge-autor badge-autor-${classeAutor}`}
                          title={
                            item.autor && item.autor !== autorCanônico
                              ? `Backend: "${item.autor}"`
                              : labelAutor
                          }
                        >
                          {labelAutor}
                        </span>
                      </td>
                      <td>
                        {item.tipo === null || item.tipo === undefined
                          ? "—"
                          : item.tipo}
                      </td>
                      <td>{item.referencia_id ?? "—"}</td>
                      <td>{item.descricao || "—"}</td>
                      <td>
                        {item.status !== undefined && item.status !== null ? (
                          <span
                            className={`historico-badge-status historico-badge-status-${classeStatus}`}
                          >
                            {item.status}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <footer className="historico-rodape">
              <strong>REGISTROS ENCONTRADOS</strong>
              <span>
                Total: <strong>{itensFiltrados.length}</strong> registro(s)
              </span>
            </footer>
          </>
        )}
      </section>
    </main>
  );
};

export default HistoricoEventos;
